import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useSetUserNotificationPreferencesMutation } from '../graphql/SetUserNotificationPreferences.generated';
import { messages } from '../NotificationPreferencesScreen.messages';
import { type NotificationCategories } from './types';

// ─────────────────────────────────────────────────────────────────────

export const useNotificationPreferencesUpdates = () => {
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  const [{ fetching }, updateNotificationPreferences] =
    useSetUserNotificationPreferencesMutation();

  // ─────────────────────────────────────────────────────────────────────

  const setNotificationPreferences = useCallback(
    async (payload: NotificationCategories) => {
      const updatedPreferences = {
        input: {
          notificationCategories: payload.map((preference) => ({
            id: preference.id,
            emailSubscribe: preference.emailSubscribe,
            pushSubscribe: preference.pushSubscribe,
          })),
        },
      };

      const result = await updateNotificationPreferences(updatedPreferences);

      const hasError =
        result.error ||
        result.data?.setUserNotificationPreferences.__typename !==
          'UserNotificationPreferences';

      if (hasError) {
        addNoticeBanner({
          text: formatMessage(messages.updateErrorText),
          palette: 'caution',
        });

        return false;
      }

      return true;
    },
    [addNoticeBanner, formatMessage, updateNotificationPreferences],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { setNotificationPreferences, isUpdating: fetching };
};
